// Used by curated trip pages (/trips/:slug) to tell <LeadBot /> what the traveller is doing.
// The page stays in charge of the trip; the bot only reads the snapshot.
import { useEffect } from 'react';
import { setLeadSignals, setLeadBotSuppressed, resetPageSignals } from './leadBotStore';

const IDLE_MS = 60000;   // a minute on the page with no tap, scroll or typing
const AWAY_MS = 30000;   // came back after 30s+ in another tab (comparing prices?)

export const useTripLeadSignals = ({
    trip,                    // tripSnapshot(): { key, slug, title, origin, start_date, end_date, pax, total, hotels, days, edits }
    agentStuck = false,      // the AI agent couldn't help twice in a row
    checkoutClosed = false,  // opened the booking summary, then closed it
    suppress = {},
    dockOffset = 0,
}) => {
    const key = trip?.key || '';

    useEffect(() => {
        setLeadSignals({ page: 'trip' });
        return () => resetPageSignals();
    }, []);

    const tripJson = JSON.stringify(trip || null);
    useEffect(() => {
        setLeadSignals({ trip: trip || null, tripKey: key });
    }, [tripJson]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setLeadSignals({ tripAgentStuck: agentStuck, tripCheckoutClosed: checkoutClosed, dockOffset });
    }, [agentStuck, checkoutClosed, dockOffset]);

    const suppressKey = JSON.stringify(suppress);
    useEffect(() => {
        setLeadBotSuppressed(suppress);
    }, [suppressKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Idle on the page
    useEffect(() => {
        setLeadSignals({ tripIdle: false });
        let timer = null;
        const arm = () => {
            clearTimeout(timer);
            timer = setTimeout(() => setLeadSignals({ tripIdle: true }), IDLE_MS);
        };
        const events = ['keydown', 'pointerdown', 'scroll', 'input'];
        events.forEach(e => document.addEventListener(e, arm, { capture: true, passive: true }));
        arm();
        return () => {
            clearTimeout(timer);
            events.forEach(e => document.removeEventListener(e, arm, { capture: true }));
        };
    }, [key]);

    // Returned after a while in another tab
    useEffect(() => {
        setLeadSignals({ tripReturned: false });
        let hiddenAt = null;
        const onVisibility = () => {
            if (document.hidden) hiddenAt = Date.now();
            else if (hiddenAt && Date.now() - hiddenAt > AWAY_MS) {
                setLeadSignals({ tripReturned: true });
                hiddenAt = null;
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [key]);
};

export default useTripLeadSignals;
